import { Plus, X } from "lucide-react"
import * as React from "react"

import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Notice } from "@/components/ui/notice"
import { ApiError, createListEntry, type ListEntry } from "@/lib/api"

/** Список подсказок к полю типа. Один на страницу — на него смотрит `list=`. */
const SUGGESTIONS_ID = "list-field-types"

/**
 * Форма заведения записи (AMEND-12 §4).
 *
 * Тип поля — **свободный ввод с подсказками**, а не выпадашка из восьми:
 * дословное требование «любое значение можно было внести» относится и к типу
 * (§2). Поэтому `<input list=…>` с `<datalist>`, а не `<select>`: восемь
 * известных имён видно в выпадашке, но чужое имя набирается руками и
 * сохраняется как введено. Выпадашка сделала бы девятый тип невозможным, и
 * человек, которому он нужен, не понял бы, что консоль его ограничила.
 *
 * Формат значения не проверяется ни длиной, ни маской — тоже §2: в самой
 * выгрузке длины внутри одного типа гуляют от 5 до 88 символов, и маска,
 * подобранная по ним, отбивала бы законные записи.
 *
 * Срок необязателен. Пустой — запись бессрочная; это подписано прямо под
 * полем, потому что пустая дата иначе читается как «забыл заполнить».
 */
export function AddValuePanel({
  list,
  knownTypes,
  onAdded,
  onClose,
}: {
  list: string
  knownTypes: string[]
  onAdded: (entry: ListEntry) => void
  onClose: () => void
}) {
  const [fieldType, setFieldType] = React.useState("")
  const [value, setValue] = React.useState("")
  const [comment, setComment] = React.useState("")
  const [expires, setExpires] = React.useState("")
  const [error, setError] = React.useState<string | null>(null)
  const [busy, setBusy] = React.useState(false)

  async function submit(event: React.FormEvent) {
    event.preventDefault()
    setBusy(true)
    setError(null)
    try {
      const entry = await createListEntry(list, {
        field_type: fieldType.trim(),
        value: value.trim(),
        comment: comment.trim() ? comment.trim() : null,
        // Поле даты отдаёт `YYYY-MM-DD`. Срок считается концом того дня по UTC:
        // возьми мы полночь его начала, запись, заведённая «до 30-го», умирала
        // бы 29-го вечером — на сутки раньше, чем человек прочитал в форме.
        expires_at: expires ? `${expires}T23:59:59Z` : null,
      })
      onAdded(entry)
    } catch (cause: unknown) {
      setError(
        cause instanceof ApiError ? cause.message : "Could not reach the Admin API.",
      )
    } finally {
      setBusy(false)
    }
  }

  return (
    <section className="animate-panel-in border-b border-line bg-surface-sunken">
      <form onSubmit={submit} className="flex flex-wrap items-end gap-2 px-4 py-3">
        <datalist id={SUGGESTIONS_ID}>
          {knownTypes.map((type) => (
            <option key={type} value={type} />
          ))}
        </datalist>

        <div className="w-44">
          <label htmlFor="entry-field-type" className="stencil-label mb-1.5 block text-fg-muted">
            Data field
          </label>
          <Input
            id="entry-field-type"
            list={SUGGESTIONS_ID}
            value={fieldType}
            required
            disabled={busy}
            autoComplete="off"
            onChange={(event) => setFieldType(event.target.value)}
            placeholder="email"
          />
        </div>

        <div className="min-w-[14rem] flex-1">
          <label htmlFor="entry-value" className="stencil-label mb-1.5 block text-fg-muted">
            Value
          </label>
          <Input
            id="entry-value"
            value={value}
            required
            disabled={busy}
            autoComplete="off"
            // Ни `type=email`, ни маски: в одном списке лежат почты, телефоны,
            // хеши карт и идентификаторы устройств (§2).
            onChange={(event) => setValue(event.target.value)}
          />
        </div>

        <div className="min-w-[12rem] flex-1">
          <label htmlFor="entry-comment" className="stencil-label mb-1.5 block text-fg-muted">
            Comment
          </label>
          <Input
            id="entry-comment"
            value={comment}
            disabled={busy}
            onChange={(event) => setComment(event.target.value)}
            placeholder="Optional"
          />
        </div>

        <div className="w-40">
          <label htmlFor="entry-expires" className="stencil-label mb-1.5 block text-fg-muted">
            Expiration
          </label>
          <Input
            id="entry-expires"
            type="date"
            value={expires}
            disabled={busy}
            onChange={(event) => setExpires(event.target.value)}
          />
        </div>

        <Button type="submit" variant="primary" disabled={busy}>
          <Plus className="h-3.5 w-3.5" strokeWidth={2.25} />
          {busy ? "Adding…" : "Add"}
        </Button>
        <Button type="button" variant="ghost" onClick={onClose} disabled={busy}>
          <X className="h-3.5 w-3.5" strokeWidth={2.25} />
          Cancel
        </Button>

        <p className="w-full text-xs leading-relaxed text-fg-muted">
          Any data field name is accepted — the eight above are suggestions, not a closed list.
          Leave <span className="font-medium text-fg">Expiration</span> empty to keep the entry
          indefinitely.
        </p>

        {error && (
          <Notice tone="error" className="w-full">
            {error}
          </Notice>
        )}
      </form>
    </section>
  )
}
